import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { FileSearch, ArrowRight, Loader2, Check, Sparkles, Pencil, Lock } from "lucide-react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { api } from "../lib/api";

const NAV = [
  { to: "/founder/dashboard", label: "Dashboard" },
  { to: "/founder/team", label: "Team" },
  { to: "/founder/support", label: "Support" },
];
const sel = "mt-1 w-full rounded-xl border bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-[#0A8B85] focus:ring-2 focus:ring-[#0A8B85]/20";
const selOk = `${sel} border-slate-200`;
const selMissing = `${sel} border-red-400 bg-red-50/40 ring-2 ring-red-200`;
const lbl = "text-xs font-bold uppercase tracking-wide";

function Field({ label, prefilled, missing, id, children }) {
  return (
    <label className={`block rounded-xl transition-colors ${missing ? "-m-2 p-2 ring-1 ring-red-200 bg-red-50/30" : ""}`} data-field={id} data-testid={`dm-field-${id}`} data-missing={missing ? "true" : "false"}>
      <span className="flex flex-wrap items-center gap-2">
        <span className={`${lbl} ${missing ? "text-red-600" : "text-slate-500"}`}>{label}</span>
        {missing ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-700" data-testid={`dm-missing-${id}`}>Required — not answered yet</span>
          : prefilled && <span className="rounded-full bg-[#0A8B85]/10 px-2 py-0.5 text-[10px] font-bold text-[#0A8B85]">Pre-filled from your quiz — edit if needed</span>}
      </span>
      {children}
    </label>
  );
}

const FIELD_LABELS = { industry: "Industry", years_operational: "Years operational", revenue_stage: "Revenue stage", ticket_size: "Ticket size",
  profitable: "Is the business profitable?", countries: "Country of operations", purposes: "Why you're raising" };

function Questionnaire({ options, initial, prefillKeys, onDone }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [showMissing, setShowMissing] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleCountry = (c) => set("countries", f.countries.includes(c) ? f.countries.filter((x) => x !== c) : [...f.countries, c]);
  const togglePurpose = (p) => set("purposes", f.purposes.includes(p) ? f.purposes.filter((x) => x !== p) : [...f.purposes, p]);
  const missing = [
    !f.industry && "industry", !f.years_operational && "years_operational", !f.revenue_stage && "revenue_stage", !f.ticket_size && "ticket_size",
    f.profitable === null && "profitable", f.countries.length === 0 && "countries", f.purposes.length === 0 && "purposes",
  ].filter(Boolean);
  const ready = missing.length === 0;
  const miss = (k) => showMissing && missing.includes(k);

  const submit = async (e) => {
    e.preventDefault();
    if (!ready) {
      setShowMissing(true);
      toast.error(`Please answer: ${missing.map((k) => FIELD_LABELS[k]).join(", ")}`);
      document.querySelector(`[data-field="${missing[0]}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/founder/direct-match", f);
      onDone(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Couldn't find matches — please try again"); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="direct-match-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="industry" label="Industry" prefilled={prefillKeys.has("industry")} missing={miss("industry")}>
          <select value={f.industry || ""} onChange={(e) => set("industry", e.target.value)} className={miss("industry") ? selMissing : selOk} data-testid="dm-industry">
            <option value="">Select your sector…</option>
            {options.industries.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </Field>
        <Field id="years_operational" label="Years operational" prefilled={prefillKeys.has("years_operational")} missing={miss("years_operational")}>
          <select value={f.years_operational || ""} onChange={(e) => set("years_operational", e.target.value)} className={miss("years_operational") ? selMissing : selOk} data-testid="dm-years">
            <option value="">Select…</option>
            {options.years.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </Field>
        <Field id="revenue_stage" label="Revenue stage" prefilled={prefillKeys.has("revenue_stage")} missing={miss("revenue_stage")}>
          <select value={f.revenue_stage || ""} onChange={(e) => set("revenue_stage", e.target.value)} className={miss("revenue_stage") ? selMissing : selOk} data-testid="dm-revenue-stage">
            <option value="">Select…</option>
            {options.revenue_stages.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </Field>
        <Field id="ticket_size" label="Ticket size looking to raise" prefilled={prefillKeys.has("ticket_size")} missing={miss("ticket_size")}>
          <select value={f.ticket_size || ""} onChange={(e) => set("ticket_size", e.target.value)} className={miss("ticket_size") ? selMissing : selOk} data-testid="dm-ticket">
            <option value="">Select…</option>
            {options.tickets.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </Field>
        <Field id="profitable" label="Is the business profitable?" prefilled={prefillKeys.has("profitable")} missing={miss("profitable")}>
          <div className="mt-1 flex gap-2">
            {[{ v: true, t: "Yes" }, { v: false, t: "No" }].map(({ v, t }) => (
              <button key={t} type="button" onClick={() => set("profitable", v)}
                className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-bold transition-colors ${f.profitable === v ? "border-[#0A8B85] bg-[#0A8B85] text-white" : miss("profitable") ? "border-red-400 bg-white text-slate-700 hover:border-[#0A8B85]" : "border-slate-200 bg-white text-slate-700 hover:border-[#0A8B85]"}`}
                data-testid={`dm-profitable-${t.toLowerCase()}`}>{t}</button>
            ))}
          </div>
        </Field>
      </div>
      <div className="mt-5">
        <Field id="countries" label="Country of operations (select all that apply)" prefilled={prefillKeys.has("countries")} missing={miss("countries")}>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="dm-countries">
            {options.countries.map((c) => {
              const on = f.countries.includes(c);
              return (
                <button key={c} type="button" onClick={() => toggleCountry(c)}
                  className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "border-[#0A8B85] bg-[#0A8B85]/10 text-[#0A8B85]" : "border-slate-200 bg-white text-slate-600 hover:border-[#0A8B85]"}`}
                  data-testid={`dm-country-${c.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
                  {on ? <Check size={12} /> : null}{c}
                </button>
              );
            })}
          </div>
        </Field>
      </div>
      <div className="mt-5">
        <Field id="purposes" label="Why are you looking to raise funds? (select all that apply)" prefilled={prefillKeys.has("purposes")} missing={miss("purposes")}>
          <p className="mt-1 text-xs text-slate-500">A bank funding stock won't look at an equity growth round, and a VC won't write a working-capital loan — this decides which funder pools we match you to.</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="dm-purposes">
            {options.purposes.map((p) => {
              const on = f.purposes.includes(p.id);
              return (
                <button key={p.id} type="button" onClick={() => togglePurpose(p.id)}
                  className={`flex items-start gap-3 rounded-xl border p-3 text-left transition-colors ${on ? "border-[#0A8B85] bg-[#0A8B85]/10" : "border-slate-200 bg-white hover:border-[#0A8B85]"}`}
                  data-testid={`dm-purpose-${p.id}`}>
                  <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${on ? "border-[#0A8B85] bg-[#0A8B85] text-white" : "border-slate-300 bg-white"}`}>{on ? <Check size={12} /> : null}</span>
                  <span>
                    <span className={`block text-sm font-bold ${on ? "text-[#0A8B85]" : "text-[#00303F]"}`}>{p.label}</span>
                    <span className="block text-xs text-slate-500">{p.hint}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </Field>
      </div>
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {ready ? <p className="text-xs text-slate-500">Your answers are saved to your profile so you never have to re-enter them.</p>
          : <p className="text-xs font-semibold text-red-600" data-testid="dm-still-needed">
            Still needed: {missing.map((k) => FIELD_LABELS[k]).join(", ")}
          </p>}
        <button type="submit" disabled={busy} aria-disabled={!ready}
          className={`inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold text-white ${ready ? "bg-[#0A8B85] hover:bg-[#097a74]" : "bg-[#0A8B85]/50 hover:bg-[#0A8B85]/60"} disabled:opacity-50`}
          data-testid="dm-submit">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {busy ? "Matching…" : "Show my funder matches"}
        </button>
      </div>
    </form>
  );
}

export const TIER_STYLE = {
  "Strong": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Possible": "bg-amber-50 text-amber-700 border-amber-200",
  "Not yet": "bg-slate-100 text-slate-600 border-slate-200",
};
const KES_PER_USD = 130;
const compact = (n) => (n >= 1e9 ? `${+(n / 1e9).toFixed(1)}B` : n >= 1e6 ? `${+(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}K` : `${Math.round(n)}`);
const range = (cur, lo, hi, mult = 1) => {
  const a = lo != null && lo >= 1 ? compact(lo * mult) : null, b = hi != null ? compact(hi * mult) : null;
  if (a != null && b != null) return a === b ? `${cur} ${a}` : `${cur} ${a} – ${b}`;
  if (a != null) return `From ${cur} ${a}`;
  return `Up to ${cur} ${b}`;
};
export const ticketLabel = (m) => {
  if (m.ticket_min == null && m.ticket_max == null) return m.ticket_range_label || "Ticket size not disclosed";
  return `${range("USD", m.ticket_min, m.ticket_max)} (≈ ${range("KES", m.ticket_min, m.ticket_max, KES_PER_USD).replace(/^Up to /, "up to ")})`;
};
const domain = (url) => { try { return new URL(url.startsWith("http") ? url : `https://${url}`).hostname; } catch { return null; } };

export function FunderLogo({ m, size = "h-12 w-12 text-lg" }) {
  const [broken, setBroken] = useState(false);
  const d = m.website ? domain(m.website) : null;
  if (d && !broken) {
    return <img src={`https://www.google.com/s2/favicons?domain=${d}&sz=128`} alt="" onError={() => setBroken(true)}
      className={`${size} rounded-xl border border-slate-200 bg-white object-contain p-1.5`} data-testid="funder-logo" />;
  }
  return <span className={`flex ${size} items-center justify-center rounded-xl bg-[#00303F] font-extrabold text-white`} data-testid="funder-logo-fallback">{(m.name || "?")[0]}</span>;
}

function FunderCard({ m, i }) {
  const nav = useNavigate();
  return (
    <div className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:items-start sm:gap-5" data-testid={`dm-match-${i}`}>
      <FunderLogo m={m} />
      <div className="mt-3 flex-1 sm:mt-0">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-extrabold text-[#00303F]">{m.name}</h3>
          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${TIER_STYLE[m.tier]}`} data-testid={`dm-tier-${i}`}>{m.tier === "Not yet" ? "Not yet" : `${m.tier} match`}</span>
          {m.apply_route === "Via Clinq" && <span className="rounded-full bg-[#0A8B85]/10 px-2 py-0.5 text-[10px] font-bold text-[#0A8B85]">Featured partner</span>}
        </div>
        <dl className="mt-2 grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
          <div><dt className="font-bold uppercase tracking-wide text-slate-400">Funds</dt><dd className="text-slate-700">{m.sectors_label || "Sector-agnostic"}{m.instruments?.length ? ` · ${m.instruments.join(" / ")}` : ""}{m.category ? ` · ${m.category}` : ""}</dd></div>
          <div><dt className="font-bold uppercase tracking-wide text-slate-400">Ticket size</dt><dd className="text-slate-700" data-testid={`dm-ticket-${i}`}>{ticketLabel(m)}</dd></div>
        </dl>
        {m.notes?.length ? <p className="mt-2 text-[11px] text-slate-500">{m.notes.join(" · ")}</p> : null}
      </div>
      <button type="button" onClick={() => nav(`/founder/apply/${m.match_id}`)}
        className={`mt-4 inline-flex shrink-0 items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-bold sm:mt-0 ${m.tier === "Not yet" ? "border border-slate-200 bg-white text-[#00303F] hover:border-[#0A8B85]" : "bg-[#0A8B85] text-white hover:bg-[#097a74]"}`}
        data-testid={`dm-apply-${i}`}>
        {m.tier === "Not yet" ? "View funder" : "Apply"} <ArrowRight size={14} />
      </button>
    </div>
  );
}

function LockedSection({ results, onUnlocked }) {
  const [busy, setBusy] = useState(false);
  const q = results.unlock;
  if (!q) return null;
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/founder/direct-match/unlock/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start payment"); setBusy(false); }
  };
  const preview = results.locked_preview || [];
  const qualifying = preview.filter((p) => p.tier !== "Not yet").length;
  const allNotYet = preview.length > 0 && qualifying === 0;
  return (
    <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="dm-locked">
      <div className="border-b border-slate-100 p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#00303F] text-white"><Lock size={16} /></span>
          <div>
            <h3 className="text-lg font-extrabold text-[#00303F]">Unlock more funders matched to your business.</h3>
            <p className="text-sm text-slate-600">
              {results.locked} more matched funder{results.locked === 1 ? "" : "s"}. The next {q.count} include{q.count === 1 ? "s" : ""}{" "}
              <b data-testid="dm-locked-qualifying">{qualifying} you currently qualify for</b>{qualifying < preview.length ? ` and ${preview.length - qualifying} to work towards` : ""}.
            </p>
          </div>
        </div>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2" data-testid="dm-locked-preview">
          {preview.map((p, i) => (
            <li key={i} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2" data-testid={`dm-locked-preview-${i}`}>
              <span className="h-8 w-8 shrink-0 rounded-lg bg-slate-200" />
              <span className="min-w-0 flex-1">
                <span className="block h-2.5 w-24 rounded bg-slate-300" aria-hidden />
                <span className="mt-1 block truncate text-[11px] text-slate-500">{p.category}{p.instruments?.length ? ` · ${p.instruments.join(" / ")}` : ""} · {p.sectors_label}</span>
              </span>
              <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold ${TIER_STYLE[p.tier]}`}>{p.tier === "Not yet" ? "Not yet" : `${p.tier} match`}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-slate-600">
          {allNotYet ? <span className="font-semibold text-amber-700" data-testid="dm-locked-warning">Heads-up: none of the next {q.count} are open to you yet — improving your score first is the better investment.</span>
            : <>Unlock the next {q.count} for <b>KES {q.price_kes.toLocaleString()}</b>{q.count < q.batch ? ` (prorated from KES 3,499 per 10)` : ""}. Names, logos and Apply buttons are revealed instantly.</>}
        </p>
        <button type="button" onClick={pay} disabled={busy}
          className={`inline-flex shrink-0 items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-bold disabled:opacity-50 ${allNotYet ? "border border-slate-300 bg-white text-[#00303F] hover:border-[#0A8B85]" : "bg-[#0A8B85] text-white hover:bg-[#097a74]"}`}
          data-testid="dm-unlock-pay">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Unlock {q.count} funder{q.count === 1 ? "" : "s"} — KES {q.price_kes.toLocaleString()}
        </button>
      </div>
      <p className="px-5 pb-4 text-[11px] text-slate-400">Secure payment via Paystack (card or M-Pesa)</p>
    </div>
  );
}

function NotYetBanner({ results, nav }) {
  const crs = results.crs_used;
  return (
    <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5" data-testid="dm-not-yet-banner">
      <h3 className="text-base font-extrabold text-[#00303F]">
        {crs != null ? `Your Capital Readiness Score is ${crs}/100 — these funders need 70+.` : "These funders aren't open to you yet."}
      </h3>
      <p className="mt-1 text-sm text-slate-700">
        Every match below fits your sector, country and ticket size — the gap is readiness, not fit. Close it and the same funders move to "Strong match".
        {crs == null ? " Applying to any funder scores your documents against their bar for free." : ""}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={() => nav("/onboarding")} className="inline-flex items-center gap-1.5 rounded-xl bg-[#0A8B85] px-4 py-2 text-sm font-bold text-white hover:bg-[#097a74]" data-testid="dm-not-yet-report">
          Get my Capital Readiness Report <ArrowRight size={14} />
        </button>
        <a href="https://prep.clinq.africa" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-bold text-[#00303F] hover:border-[#0A8B85]" data-testid="dm-not-yet-prep">
          Build missing documents in Clinq Prep
        </a>
      </div>
    </div>
  );
}

function Matches({ results, answers, options, onEdit, onUnlocked }) {
  const nav = useNavigate();
  const label = (list, id) => list.find((o) => o.id === id)?.label || id;
  const { funders, total, tiers } = results;
  return (
    <div data-testid="direct-match-results">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4">
        <p className="text-xs text-slate-600">
          <b className="text-slate-800">{label(options.industries, answers.industry)}</b> · {label(options.revenue_stages, answers.revenue_stage)}{answers.profitable ? ", profitable" : ""} · {label(options.tickets, answers.ticket_size)} · {answers.countries.join(", ")}{answers.purposes?.length ? <> · <span data-testid="dm-summary-purposes">{answers.purposes.map((p) => label(options.purposes, p)).join(", ")}</span></> : null}
        </p>
        <button type="button" onClick={onEdit} className="inline-flex items-center gap-1 text-xs font-bold text-[#0A8B85] hover:underline" data-testid="dm-edit">
          <Pencil size={12} /> Edit answers
        </button>
      </div>
      {total === 0 ? (
        <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-white p-8 text-center" data-testid="dm-no-matches">
          <h2 className="text-lg font-extrabold text-[#00303F]">No funders match these answers yet</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-500">Try widening your countries or ticket size, or let Clinq assess your business and recommend funders through the guided path.</p>
        </div>
      ) : (
        <>
          <p className="mt-6 text-[11px] font-bold uppercase tracking-wider text-[#0A8B85]" data-testid="dm-summary">
            {total} matched funder{total === 1 ? "" : "s"} · {tiers.Strong} strong · {tiers.Possible} possible · {tiers["Not yet"]} not yet · showing {funders.length}
          </p>
          {tiers.Strong + tiers.Possible === 0 && <NotYetBanner results={results} nav={nav} />}
          <div className="mt-2 space-y-3">
            {funders.map((m, i) => <FunderCard key={m.match_id ?? i} m={m} i={i} />)}
          </div>
          <LockedSection results={results} onUnlocked={onUnlocked} />
        </>
      )}
    </div>
  );
}

export default function FounderApply() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState(null);
  const [editing, setEditing] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/founder/direct-match/options")
      .then(({ data }) => { setState(data); if (data.saved && data.results) setResult({ saved: data.saved, results: data.results }); })
      .catch(() => toast.error("Couldn't load the questionnaire"));
  }, []);

  // Back from Paystack → confirm payment and reveal the next batch
  useEffect(() => {
    const ref = params.get("unlock_ref");
    if (!ref) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.post(`/founder/direct-match/unlock/poll/${ref}`);
        if (data.payment_status === "paid") {
          setResult((p) => ({ saved: p?.saved || state?.saved, results: data.results }));
          toast.success(`Payment received — ${data.results.unlocked} funders now unlocked`);
          params.delete("unlock_ref"); setParams(params, { replace: true }); return;
        }
        if (data.payment_status === "failed") { toast.error("Payment was not completed"); params.delete("unlock_ref"); setParams(params, { replace: true }); return; }
      } catch { /* retry */ }
      if (++tries < 8) setTimeout(poll, 2500);
    };
    poll();
  }, [params.get("unlock_ref")]); // eslint-disable-line react-hooks/exhaustive-deps

  const initial = useMemo(() => {
    if (!state) return null;
    const base = state.saved || state.prefill || {};
    return {
      industry: base.industry || "", years_operational: base.years_operational || "",
      revenue_stage: base.revenue_stage || "", ticket_size: base.ticket_size || "",
      profitable: base.profitable ?? null, countries: base.countries || [],
      purposes: base.purposes || (state.saved ? (state.prefill?.purposes || []) : []),
    };
  }, [state]);
  const prefillKeys = useMemo(() => {
    if (!state || state.saved) return new Set();
    return new Set(Object.entries(state.prefill || {}).filter(([, v]) => v !== null && v !== "" && !(Array.isArray(v) && !v.length)).map(([k]) => k));
  }, [state]);

  const showForm = state && (!result || editing);

  if (user?.company_kind === "project") return <Navigate to="/project/report" replace />;
  return (
    <AppShell nav={NAV}>
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6" data-testid="founder-apply">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#0A8B85]">Direct applications</p>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-[#00303F] sm:text-4xl">
          {showForm ? "Tell us what you're looking for" : "Your funder matches"}
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-600 sm:text-base">
          {showForm
            ? "Seven quick questions so we only show you funders that actually fit. Anything we already know from your Get Started quiz is filled in — change whatever's out of date."
            : "Ranked by how well each funder fits your answers. Strong matches first, then possible matches with their caveats, then funders you're not quite ready for yet."}
        </p>

        <div className="mt-8">
          {!state && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0A8B85]" /></div>}
          {showForm && (
            <Questionnaire options={state.options} initial={initial} prefillKeys={prefillKeys}
              onDone={(data) => { setResult(data); setEditing(false); toast.success(`${data.results.total} funder match${data.results.total === 1 ? "" : "es"} found`); }} />
          )}
          {state && result && !editing && (
            <Matches results={result.results} answers={result.saved} options={state.options} onEdit={() => setEditing(true)}
              onUnlocked={(r) => setResult((p) => ({ ...p, results: r }))} />
          )}
        </div>

        <div className="mt-8 flex flex-col items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-[#0A8B85] shadow-sm"><FileSearch size={18} /></span>
            <div>
              <p className="text-sm font-extrabold text-[#00303F]">Prefer to be guided?</p>
              <p className="text-xs text-slate-500">Get your Capital Readiness Report and let Clinq recommend the right funders for you.</p>
            </div>
          </div>
          <button type="button" onClick={() => nav("/onboarding")}
            className="inline-flex items-center gap-2 rounded-xl bg-[#0A8B85] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#097a74]"
            data-testid="founder-apply-switch-guided">
            Switch to the guided path <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </AppShell>
  );
}
